import { useState, useEffect, useCallback } from 'react';
import { hotelApi } from '../../hotelApi';
import { fmtDate } from '../../dateUtils';

const today = () => new Date().toISOString().split('T')[0];

const EMPTY_TB = { tableId: '', customerName: '', customerPhone: '', bookingDate: today(), bookingTime: '', numGuests: 2, status: 'confirmed', notes: '' };
const EMPTY_RB = { roomId: '', customerName: '', customerPhone: '', checkIn: today(), checkOut: '', numGuests: 1, totalAmount: '', advancePaid: 0, status: 'confirmed', notes: '' };

function StatusBadge({ s }) {
  const map = {
    confirmed: ['#e6f4ea','#137333'],
    cancelled: ['#fce8e6','#c5221f'],
    completed: ['#e8f0fe','#1a73e8'],
    'checked-in': ['#e6f4ea','#0d7230'],
  };
  const [bg, col] = map[s] || ['#f1f3f4','#888'];
  return <span style={{ background: bg, color: col, borderRadius: 10, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{s}</span>;
}

export default function HotelBookings({ role }) {
  const [tab, setTab]           = useState('tables');
  const [selectedDate, setSelectedDate] = useState(today());
  const [tableBookings, setTableBookings] = useState([]);
  const [roomBookings, setRoomBookings]   = useState([]);
  const [tables, setTables]     = useState([]);
  const [rooms, setRooms]       = useState([]);
  const [allTableBookings, setAllTableBookings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showTBForm, setShowTBForm] = useState(false);
  const [showRBForm, setShowRBForm] = useState(false);
  const [tbForm, setTBForm]     = useState(EMPTY_TB);
  const [rbForm, setRBForm]     = useState(EMPTY_RB);
  const [editTBId, setEditTBId] = useState(null);
  const [editRBId, setEditRBId] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const proms = tab === 'tables'
      ? [
          hotelApi.getTableBookings(selectedDate),
          hotelApi.getTableBookings(),        // all — for conflict checking
          Promise.resolve([]),
          hotelApi.getTables(),
          Promise.resolve([]),
        ]
      : [
          Promise.resolve([]),
          Promise.resolve(allTableBookings),  // keep existing
          hotelApi.getRoomBookings(),
          Promise.resolve([]),
          hotelApi.getRooms(),
        ];
    Promise.all(proms).then(([tb, allTb, rb, t, r]) => {
      setTableBookings(tb || []);
      if (tab === 'tables') setAllTableBookings(allTb || []);
      setRoomBookings(rb || []);
      setTables(t || []);
      setRooms(r || []);
    }).finally(() => setLoading(false));
  }, [tab, selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const fmtRs = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

  // Same table + same date = conflict unless both times are set and different (separate seatings)
  const tableConflict = (tableId, bookingDate, bookingTime, excludeId) => {
    if (!tableId || !bookingDate) return null;
    return allTableBookings.find(b => {
      if (String(b.table_id) !== String(tableId)) return false;
      if (b.status === 'cancelled') return false;
      if (excludeId && String(b.id) === String(excludeId)) return false;
      if (b.booking_date !== bookingDate) return false;
      // Only allow if BOTH bookings have explicit times and they're different
      if (bookingTime && b.booking_time && bookingTime !== b.booking_time) return false;
      return true;
    }) || null;
  };

  // Table bookings CRUD
  const saveTB = async () => {
    if (!tbForm.customerName.trim()) { setErr('Customer name required'); return; }
    if (!tbForm.tableId) { setErr('Please select a table'); return; }
    const conflict = tableConflict(tbForm.tableId, tbForm.bookingDate, tbForm.bookingTime, editTBId);
    if (conflict) {
      setErr(`Table is already booked on ${fmtDate(conflict.booking_date)}${conflict.booking_time ? ' at ' + conflict.booking_time : ''} by ${conflict.customer_name}. Choose a different table or time.`);
      return;
    }
    setSaving(true); setErr('');
    try {
      if (editTBId) await hotelApi.updateTableBooking(editTBId, tbForm);
      else await hotelApi.createTableBooking(tbForm);
      setTBForm(EMPTY_TB); setEditTBId(null); setShowTBForm(false); load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const deleteTB = async (id) => {
    if (!window.confirm('Cancel this booking?')) return;
    await hotelApi.deleteTableBooking(id); load();
  };

  const roomConflict = (roomId, checkIn, checkOut, excludeId) => {
    if (!roomId || !checkIn || !checkOut || checkIn >= checkOut) return null;
    return roomBookings.find(b => {
      if (String(b.room_id) !== String(roomId)) return false;
      if (b.status === 'cancelled') return false;
      if (excludeId && String(b.id) === String(excludeId)) return false;
      return checkIn < b.check_out && checkOut > b.check_in;
    }) || null;
  };

  // Room bookings CRUD
  const saveRB = async () => {
    if (!rbForm.customerName.trim()) { setErr('Customer name required'); return; }
    if (!rbForm.roomId) { setErr('Please select a room'); return; }
    if (!rbForm.checkIn || !rbForm.checkOut) { setErr('Check-in and check-out dates required'); return; }
    if (rbForm.checkIn >= rbForm.checkOut) { setErr('Check-out must be after check-in'); return; }
    const conflict = roomConflict(rbForm.roomId, rbForm.checkIn, rbForm.checkOut, editRBId);
    if (conflict) {
      setErr(`Room is already booked from ${fmtDate(conflict.check_in)} to ${fmtDate(conflict.check_out)} (${conflict.customer_name}). Choose different dates or room.`);
      return;
    }
    setSaving(true); setErr('');
    try {
      if (editRBId) await hotelApi.updateRoomBooking(editRBId, rbForm);
      else await hotelApi.createRoomBooking(rbForm);
      setRBForm(EMPTY_RB); setEditRBId(null); setShowRBForm(false); load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const deleteRB = async (id) => {
    if (!window.confirm('Cancel this room booking?')) return;
    await hotelApi.deleteRoomBooking(id); load();
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading bookings…</div>;

  return (
    <div>
      <div className="tabs" style={{ marginBottom: 16 }}>
        <button className={`tab${tab === 'tables' ? ' active' : ''}`} onClick={() => setTab('tables')}>
          🍽️ Table Bookings
        </button>
        <button className={`tab${tab === 'rooms' ? ' active' : ''}`} onClick={() => setTab('rooms')}>
          🛏️ Room Bookings
        </button>
      </div>

      {/* ── Table Bookings ────────────────────────────────────────── */}
      {tab === 'tables' && (
        <div className="card">
          <div className="card-header" style={{ flexWrap: 'wrap', gap: 10 }}>
            <div className="card-title">Table Bookings</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label style={{ fontSize: 13, color: '#5f6368' }}>Date:</label>
              <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
                className="form-control" style={{ maxWidth: 160, fontSize: 13 }} />
              <button className="btn btn-primary" style={{ fontSize: 13, padding: '5px 14px' }}
                onClick={() => { setTBForm({ ...EMPTY_TB, bookingDate: selectedDate }); setEditTBId(null); setShowTBForm(true); }}>
                ➕ Add Booking
              </button>
            </div>
          </div>

          {showTBForm && (() => {
            const tbConflict = tableConflict(tbForm.tableId, tbForm.bookingDate, tbForm.bookingTime, editTBId);
            return (
              <div className="card-body" style={{ borderBottom: '1px solid #e8eaed', background: '#f8faf8' }}>
                {err && <div style={{ color: '#c5221f', marginBottom: 10, fontSize: 13, background: '#fce8e6',
                  border: '1px solid #f5c6c6', borderRadius: 8, padding: '8px 12px' }}>⚠️ {err}</div>}

                {/* Live conflict warning */}
                {tbConflict && (
                  <div style={{ marginBottom: 12, background: '#fce8e6', border: '1px solid #f5c6c6',
                    borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#c5221f', display: 'flex', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>🚫</span>
                    <span>
                      <strong>Table not available</strong> — already booked on{' '}
                      <strong>{fmtDate(tbConflict.booking_date)}</strong>
                      {tbConflict.booking_time ? <> at <strong>{tbConflict.booking_time}</strong></> : ''}{' '}
                      by <strong>{tbConflict.customer_name}</strong>.
                      Choose a different table or time slot.
                    </span>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Customer Name *</label>
                    <input className="form-control" value={tbForm.customerName}
                      onChange={e => setTBForm(f => ({ ...f, customerName: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input className="form-control" value={tbForm.customerPhone}
                      onChange={e => setTBForm(f => ({ ...f, customerPhone: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Table *</label>
                    <select className="form-control" value={tbForm.tableId}
                      onChange={e => setTBForm(f => ({ ...f, tableId: e.target.value }))}
                      style={{}}>
                      <option value="">— Select Table —</option>
                      {tables.map(t => {
                        const booked = tableConflict(t.id, tbForm.bookingDate, tbForm.bookingTime, editTBId);
                        return (
                          <option key={t.id} value={t.id} disabled={!!booked}>
                            Table {t.table_no}{t.section ? ` (${t.section})` : ''}{t.capacity ? ` · ${t.capacity} seats` : ''}{booked ? ' 🚫 Booked' : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Date *</label>
                    <input className="form-control" type="date" value={tbForm.bookingDate}
                      onChange={e => setTBForm(f => ({ ...f, bookingDate: e.target.value }))}
                      style={{}} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Time</label>
                    <input className="form-control" type="time" value={tbForm.bookingTime}
                      onChange={e => setTBForm(f => ({ ...f, bookingTime: e.target.value }))} />
                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                      Set different times to allow multiple seatings per day
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Guests</label>
                    <input className="form-control" type="number" value={tbForm.numGuests}
                      onChange={e => setTBForm(f => ({ ...f, numGuests: e.target.value }))} min={1} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-control" value={tbForm.status}
                      onChange={e => setTBForm(f => ({ ...f, status: e.target.value }))}>
                      {['confirmed','cancelled','completed'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Notes</label>
                    <input className="form-control" value={tbForm.notes}
                      onChange={e => setTBForm(f => ({ ...f, notes: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="btn btn-primary" onClick={saveTB} disabled={saving || !!tbConflict}>
                    {saving ? 'Saving…' : editTBId ? '💾 Update' : '➕ Book'}
                  </button>
                  <button className="btn btn-secondary"
                    onClick={() => { setShowTBForm(false); setEditTBId(null); setTBForm(EMPTY_TB); setErr(''); }}>
                    Cancel
                  </button>
                </div>
              </div>
            );
          })()}

          <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th><th>Phone</th><th>Table</th>
                  <th>Date</th><th>Time</th><th>Guests</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tableBookings.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: '#888', padding: 24 }}>
                    No bookings for {fmtDate(selectedDate)}
                  </td></tr>
                )}
                {tableBookings.map(b => (
                  <tr key={b.id}>
                    <td><strong>{b.customer_name}</strong></td>
                    <td style={{ fontSize: 13 }}>{b.customer_phone || '—'}</td>
                    <td>{b.table_no ? `Table ${b.table_no}` : '—'}</td>
                    <td style={{ fontSize: 13 }}>{fmtDate(b.booking_date)}</td>
                    <td style={{ fontSize: 13 }}>{b.booking_time || '—'}</td>
                    <td>{b.num_guests}</td>
                    <td><StatusBadge s={b.status} /></td>
                    <td>
                      <button className="btn-sm btn-secondary" style={{ marginRight: 4 }}
                        onClick={() => {
                          setTBForm({ tableId: b.table_id || '', customerName: b.customer_name,
                            customerPhone: b.customer_phone || '', bookingDate: b.booking_date,
                            bookingTime: b.booking_time || '', numGuests: b.num_guests,
                            status: b.status, notes: b.notes || '' });
                          setEditTBId(b.id); setShowTBForm(true);
                        }}>Edit</button>
                      <button className="btn-sm btn-danger" onClick={() => deleteTB(b.id)}>Cancel</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Room Bookings ─────────────────────────────────────────── */}
      {tab === 'rooms' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Room Bookings</div>
            <button className="btn btn-primary" style={{ fontSize: 13, padding: '5px 14px' }}
              onClick={() => { setRBForm(EMPTY_RB); setEditRBId(null); setShowRBForm(true); }}>
              ➕ Add Booking
            </button>
          </div>

          {showRBForm && (() => {
            const selRoom = rooms.find(r => String(r.id) === String(rbForm.roomId));
            const nights = (rbForm.checkIn && rbForm.checkOut && rbForm.checkOut > rbForm.checkIn)
              ? Math.round((new Date(rbForm.checkOut) - new Date(rbForm.checkIn)) / 86400000) : 0;
            const ratePerNight = Number(selRoom?.rate_per_night || 0);
            const subtotal = nights > 0 && ratePerNight > 0 ? nights * ratePerNight : 0;
            const conflict = roomConflict(rbForm.roomId, rbForm.checkIn, rbForm.checkOut, editRBId);

            const handleRoomChange = (roomId) => {
              const r = rooms.find(x => String(x.id) === String(roomId));
              const n = (rbForm.checkIn && rbForm.checkOut && rbForm.checkOut > rbForm.checkIn)
                ? Math.round((new Date(rbForm.checkOut) - new Date(rbForm.checkIn)) / 86400000) : 0;
              const auto = r && n > 0 ? n * Number(r.rate_per_night || 0) : '';
              setRBForm(f => ({ ...f, roomId, totalAmount: auto || f.totalAmount }));
            };

            const handleDateChange = (field, val) => {
              setRBForm(f => {
                const updated = { ...f, [field]: val };
                const checkIn  = field === 'checkIn'  ? val : f.checkIn;
                const checkOut = field === 'checkOut' ? val : f.checkOut;
                const r = rooms.find(x => String(x.id) === String(f.roomId));
                const n = (checkIn && checkOut && checkOut > checkIn)
                  ? Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000) : 0;
                if (r && n > 0) updated.totalAmount = n * Number(r.rate_per_night || 0);
                return updated;
              });
            };

            return (
              <div className="card-body" style={{ borderBottom: '1px solid #e8eaed', background: '#f8faf8' }}>
                {err && <div style={{ color: '#c5221f', marginBottom: 10, fontSize: 13, background: '#fce8e6',
                  border: '1px solid #f5c6c6', borderRadius: 8, padding: '8px 12px' }}>⚠️ {err}</div>}

                {/* Conflict warning */}
                {conflict && (
                  <div style={{ marginBottom: 12, background: '#fce8e6', border: '1px solid #f5c6c6',
                    borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#c5221f', display: 'flex', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>🚫</span>
                    <span>
                      <strong>Room not available</strong> — already booked by <strong>{conflict.customer_name}</strong> from{' '}
                      <strong>{fmtDate(conflict.check_in)}</strong> to <strong>{fmtDate(conflict.check_out)}</strong>.
                      Please choose different dates or a different room.
                    </span>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Customer Name *</label>
                    <input className="form-control" value={rbForm.customerName}
                      onChange={e => setRBForm(f => ({ ...f, customerName: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input className="form-control" value={rbForm.customerPhone}
                      onChange={e => setRBForm(f => ({ ...f, customerPhone: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Room *</label>
                    <select className="form-control" value={rbForm.roomId}
                      onChange={e => handleRoomChange(e.target.value)}
                      style={{}}>
                      <option value="">— Select Room —</option>
                      {rooms.map(r => {
                        const occupied = roomConflict(r.id, rbForm.checkIn, rbForm.checkOut, editRBId);
                        return (
                          <option key={r.id} value={r.id} disabled={!!occupied}>
                            Room {r.room_no} ({r.room_type}){r.rate_per_night ? ` · ₹${Number(r.rate_per_night).toLocaleString('en-IN')}/night` : ''}{occupied ? ' 🚫 Booked' : ''}
                          </option>
                        );
                      })}
                    </select>
                    {selRoom && <div style={{ fontSize: 11, color: '#137333', marginTop: 3 }}>
                      Rate: ₹{Number(selRoom.rate_per_night || 0).toLocaleString('en-IN')}/night
                    </div>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Check In *</label>
                    <input className="form-control" type="date" value={rbForm.checkIn}
                      onChange={e => handleDateChange('checkIn', e.target.value)}
                      style={{}} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Check Out *</label>
                    <input className="form-control" type="date" value={rbForm.checkOut}
                      min={rbForm.checkIn || undefined}
                      onChange={e => handleDateChange('checkOut', e.target.value)}
                      style={{}} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Guests</label>
                    <input className="form-control" type="number" value={rbForm.numGuests}
                      onChange={e => setRBForm(f => ({ ...f, numGuests: e.target.value }))} min={1} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Total Amount (₹)</label>
                    <input className="form-control" type="number" value={rbForm.totalAmount}
                      onChange={e => setRBForm(f => ({ ...f, totalAmount: e.target.value }))}
                      placeholder="Auto-calculated" />
                    {subtotal > 0 && String(rbForm.totalAmount) !== String(subtotal) && (
                      <div style={{ fontSize: 11, color: '#b45309', marginTop: 3 }}>
                        Calculated: ₹{subtotal.toLocaleString('en-IN')} (edited manually)
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Advance Paid (₹)</label>
                    <input className="form-control" type="number" value={rbForm.advancePaid}
                      onChange={e => setRBForm(f => ({ ...f, advancePaid: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-control" value={rbForm.status}
                      onChange={e => setRBForm(f => ({ ...f, status: e.target.value }))}>
                      {['confirmed','checked-in','checked-out','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Notes</label>
                    <input className="form-control" value={rbForm.notes}
                      onChange={e => setRBForm(f => ({ ...f, notes: e.target.value }))} />
                  </div>
                </div>

                {/* Cost summary panel */}
                {selRoom && nights > 0 && (
                  <div style={{ marginTop: 16, background: '#f0faf4', border: '1px solid #b7dfbe',
                    borderRadius: 10, padding: '14px 18px' }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#0d3321', marginBottom: 10 }}>
                      📊 Booking Summary
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 32px', fontSize: 13, color: '#374151' }}>
                      <span>Room: <strong>{selRoom.room_no} ({selRoom.room_type})</strong></span>
                      <span>Rate: <strong>₹{ratePerNight.toLocaleString('en-IN')}/night</strong></span>
                      <span>Nights: <strong>{nights}</strong></span>
                    </div>
                    <div style={{ marginTop: 12, borderTop: '1px solid #b7dfbe', paddingTop: 10,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, color: '#5f6368' }}>Subtotal (before taxes)</span>
                      <span style={{ fontSize: 20, fontWeight: 800, color: '#137333' }}>
                        ₹{subtotal.toLocaleString('en-IN')}
                      </span>
                    </div>
                    {Number(rbForm.advancePaid) > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13,
                        color: '#5f6368', marginTop: 6 }}>
                        <span>Advance paid</span>
                        <span style={{ color: '#1a73e8', fontWeight: 600 }}>
                          − ₹{Number(rbForm.advancePaid).toLocaleString('en-IN')}
                        </span>
                      </div>
                    )}
                    {Number(rbForm.advancePaid) > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14,
                        fontWeight: 700, color: '#202124', marginTop: 4, borderTop: '1px solid #b7dfbe', paddingTop: 6 }}>
                        <span>Balance due</span>
                        <span>₹{Math.max(0, subtotal - Number(rbForm.advancePaid)).toLocaleString('en-IN')}</span>
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button className="btn btn-primary" onClick={saveRB} disabled={saving || !!conflict}>
                    {saving ? 'Saving…' : editRBId ? '💾 Update' : '➕ Book Room'}
                  </button>
                  <button className="btn btn-secondary"
                    onClick={() => { setShowRBForm(false); setEditRBId(null); setRBForm(EMPTY_RB); setErr(''); }}>
                    Cancel
                  </button>
                </div>
              </div>
            );
          })()}

          <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th><th>Phone</th><th>Room</th>
                  <th>Check In</th><th>Check Out</th><th>Guests</th>
                  <th>Total</th><th>Advance</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {roomBookings.length === 0 && (
                  <tr><td colSpan={10} style={{ textAlign: 'center', color: '#888', padding: 24 }}>
                    No upcoming room bookings
                  </td></tr>
                )}
                {roomBookings.map(b => (
                  <tr key={b.id}>
                    <td><strong>{b.customer_name}</strong></td>
                    <td style={{ fontSize: 13 }}>{b.customer_phone || '—'}</td>
                    <td>{b.room_no ? `${b.room_no} (${b.room_type})` : '—'}</td>
                    <td style={{ fontSize: 13 }}>{fmtDate(b.check_in)}</td>
                    <td style={{ fontSize: 13 }}>{fmtDate(b.check_out)}</td>
                    <td>{b.num_guests}</td>
                    <td style={{ fontWeight: 600 }}>{fmtRs(b.total_amount)}</td>
                    <td style={{ color: '#137333' }}>{fmtRs(b.advance_paid)}</td>
                    <td><StatusBadge s={b.status} /></td>
                    <td>
                      <button className="btn-sm btn-secondary" style={{ marginRight: 4 }}
                        onClick={() => {
                          setRBForm({ roomId: b.room_id || '', customerName: b.customer_name,
                            customerPhone: b.customer_phone || '', checkIn: b.check_in,
                            checkOut: b.check_out, numGuests: b.num_guests,
                            totalAmount: b.total_amount || '', advancePaid: b.advance_paid || 0,
                            status: b.status, notes: b.notes || '' });
                          setEditRBId(b.id); setShowRBForm(true);
                        }}>Edit</button>
                      <button className="btn-sm btn-danger" onClick={() => deleteRB(b.id)}>Cancel</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
